'use client';

import {AnimatePresence, motion, Variants} from 'motion/react';
import {
  ButtonHTMLAttributes,
  forwardRef,
  MouseEvent,
  useCallback,
  useEffect,
  useState,
} from 'react';
import {tv, VariantProps} from 'tailwind-variants';

import {cn, getMotionProps} from '../../libs';
import {Loader} from '../Loader';

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    Omit<ButtonVariants, 'loading'> {
  loading?: boolean;
}

type ButtonVariants = VariantProps<typeof button>;

const button = tv({
  base: [
    'relative inline-flex cursor-pointer items-center justify-center gap-2 rounded-md border text-paragraph-sm transition-all',
    'disabled:pointer-events-none',
  ],
  variants: {
    variant: {
      primary: [
        'bg-brand-secondary',
        'border-brand-secondary',
        'text-text-contrast',
        'hover:bg-brand-secondary-hover',
        'hover:border-brand-secondary-hover',
        'disabled:bg-bg-secondary-hover',
        'disabled:border-bg-secondary-hover',
        'disabled:text-text-disabled',
      ],
      'primary-extension': [
        'bg-ext-lime-primary',
        'border-ext-lime-primary',
        'text-text-contrast',
        'hover:bg-ext-lime-primary-hover',
        'hover:border-ext-lime-primary-hover',
      ],
      outline: [
        'bg-bg-secondary',
        'border-input-border',
        'text-text-secondary',
        'hover:bg-bg-secondary-hover',
        'hover:border-input-border-hover',
        'disabled:bg-bg-secondary-hover',
        'disabled:text-text-disabled',
      ],
      danger: [
        'bg-error-primary',
        'border-error-primary',
        'text-text-contrast',
        'hover:bg-error-primary-hover',
        'hover:border-error-primary-hover',
        'disabled:opacity-50',
      ],
      link: [
        'border-none',
        'text-brand-secondary',
        'hover:text-brand-secondary-hover',
        'disabled:text-text-disabled',
      ],
    },
    loading: {
      true: 'pointer-events-none',
    },
    disabled: {
      true: 'pointer-events-none',
    },
    size: {
      sm: 'h-8 px-3 text-xs',
      md: 'h-10 px-4 text-sm',
      icon: 'size-10 justify-center text-center text-sm',
    },
    shape: {
      square: 'rounded-md px-0',
    },
  },
  compoundVariants: [
    {
      variant: 'link',
      size: 'md',
      className: 'size-fit p-0',
    },
    {
      size: 'sm',
      shape: 'square',
      className: 'size-8',
    },
    {
      size: 'md',
      shape: 'square',
      className: 'size-10',
    },
    {
      size: 'icon',
      shape: 'square',
      className: 'size-10',
    },
  ],
});

const loadingMotionProps = getMotionProps({
  initial: {y: -4, opacity: 0},
  animate: {y: 0, opacity: 1},
  exit: {y: -4, opacity: 0},
});

const entryMotionVariants: Variants = {
  default: {},
  loading: {
    y: 4,
    opacity: 0,
    transition: {duration: 0.12, type: 'tween'},
  },
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {children, variant = 'primary', size = 'md', shape, loading, type = 'button', onClick, ...rest},
  ref,
) {
  const [computedLoading, setComputedLoading] = useState(loading);

  useEffect(() => {
    setComputedLoading(loading);
  }, [loading]);

  const onPromisedClick = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      if (!onClick) return;
      if (onClick.constructor.name === 'AsyncFunction') {
        const promisedOnClick = onClick as (e: MouseEvent<HTMLButtonElement>) => Promise<void>;
        setComputedLoading(true);
        void promisedOnClick(e).finally(() => setComputedLoading(false));
      } else {
        onClick(e);
      }
    },
    [onClick],
  );

  return (
    <button
      {...rest}
      ref={ref}
      type={type}
      className={cn(
        button({variant, loading: !!computedLoading, size, shape, disabled: rest.disabled}),
        rest.className,
      )}
      onClick={onPromisedClick}
    >
      <AnimatePresence mode="wait">
        {computedLoading && (
          <motion.div
            {...loadingMotionProps}
            className="absolute top-0 left-0 z-10 flex size-full items-center justify-center gap-2"
          >
            <Loader size={16} />
          </motion.div>
        )}
      </AnimatePresence>

      <motion.div
        className="flex items-center justify-center gap-2 whitespace-nowrap text-paragraph-sm"
        variants={entryMotionVariants}
        animate={computedLoading ? 'loading' : 'default'}
      >
        {children}
      </motion.div>
    </button>
  );
});
